/**
 * WHEN IS THE BATCH REVIEW SCOPE SETTLED ENOUGH TO FETCH?
 *
 * Batch Review loads on a Client + State scope. Both can arrive from the URL — `?partnersId`,
 * `?stateId` (the round-trip back from Client Filing Defaults) and `?CustomerPolicyId` (the "View
 * Extraction Details" deep link, whose client + state are resolved by a lookup) — and each is applied
 * by its own asynchronous effect fed by its own promise.
 *
 * ⚠️ THE COST OF NOT WAITING IS A FULL EXTRA BATCH FETCH PER PROMISE, AND THAT FETCH IS ~3 SECONDS.
 * Without a gate the load effect fires on mount with the unscoped `partnersId=0&stateId=0` — the
 * whole book, the most expensive query the screen can ask for — then again when the partner lands,
 * then again when the state lands. Three overlapping requests, of which only the last is the one the
 * filer asked for; the first two are discarded by the stale-response guard AFTER the server has done
 * all the work. React 18 cannot batch them away, because they are settled by separate promises in
 * separate microtasks rather than inside one event handler.
 *
 * So the rule is: if the URL PROMISES a narrower scope, wait for that promise to be kept (or broken)
 * before fetching anything. If it promises nothing, the unscoped load is the correct load and fires
 * immediately — that default view is deliberate (Jerry, 2026-07-16), and delaying it would be a
 * regression, not a saving.
 *
 * ⚠️ "RESOLVED" MEANS SETTLED, NOT SUCCEEDED. Every resolution path — found, not found, request
 * failed, list came back empty — must mark itself resolved. A flag that is only raised on success
 * turns a failed lookup into a screen that never loads anything at all, which is a far worse failure
 * than one redundant request. The tests below pin that.
 */

export interface BatchReviewScopeResolution {
    /** `?partnersId` was on the URL when the screen was opened. */
    hasPartnerParam: boolean;
    /** `?stateId` was on the URL when the screen was opened. */
    hasStateParam: boolean;
    /** `?CustomerPolicyId` was on the URL when the screen was opened (resolves BOTH client and state). */
    hasPolicyParam: boolean;
    /** The client preselect has run to a conclusion — applied, or determined not applicable. */
    partnerResolved: boolean;
    /** The state preselect has run to a conclusion. */
    stateResolved: boolean;
    /** The deep-linked policy lookup has run to a conclusion. */
    policyResolved: boolean;
}

/**
 * True when nothing on the URL is still promising a narrower scope, so a fetch issued now will be
 * issued with the scope the filer actually asked for.
 */
export function isScopeSettled(scope: BatchReviewScopeResolution): boolean {
    if (scope.hasPartnerParam && !scope.partnerResolved) return false;
    if (scope.hasStateParam && !scope.stateResolved) return false;
    if (scope.hasPolicyParam && !scope.policyResolved) return false;
    return true;
}
